import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { diff } from 'deep-object-diff';
import dropsLoader from '@/mixins/components/drops-loader';
import newSection from '@/payloads/section.json';
import newPage from '@/payloads/page.json';
import { guid } from '@/mixins/guid';
import { siteBuilder } from '@/mixins/site-builder';

export default Vue.component('add-page', {
  template: require('./template.html'),
  data() {
    return {
      paginate: ['templates'],
      searchTemplates: '',
      name: '',
      pageNameCheckError: false,
      loading: false,
      templateID: false,
    };
  },
  mixins: [guid, siteBuilder],
  components: {
    dropsLoader,
  },
  computed: {
    ...mapGetters([
      'currentPage',
      'initialCurrentPage',
      'currentSite',
    ]),
    layouts() {
      const raw = this._currentSite.templates;
      return _.map(raw, l => _.assign({}, l, { selected: false }));
    },
    savedPages() {
      return _.map(this._currentSite.pages, 'name');
    },
    templates() {
      const searchInput = new RegExp(this.searchTemplates, 'i');
      return this.layouts.filter(layout => layout.name.match(searchInput));
    },
  },
  props: [
    'toggle-modal',
    'unsavedChanges',
    'change-unsaved',
  ],
  methods: {
    nameCheck(e) {
      let val = e.currentTarget.value;
      val = val.replace(/[^a-zA-Z0-9|\s]/g, '-');
      this.name = val;
    },
    clearSelectedLayouts(name) {
      _.forEach(this.layouts, (l) => {
        if (l.name !== name) {
          l.selected = false;
        }
      });
    },
    toggleLayoutSelected(layout) {
      this.clearSelectedLayouts(layout.name);
      this.templateID = layout._id;
      layout.selected = !layout.selected;
    },
    // run front end validation
    async submit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          // after quick validations run, run check for duplicate names
          this.pageNameCheck(this.name).then(async (noDuplicates) => {
            if (noDuplicates) {
              // first check if there are any unsaved changes to the current page
              const cP = this._deepOmit(this.currentPage, this._pageDiffIgnore);
              const iP = this._deepOmit(this.initialCurrentPage, this._pageDiffIgnore);
              if (_.isEqual(cP, iP)) {
                // proceed with new page
                await this.goToNew();
              } else {
                // unsaved changes present
                this.$emit('change-unsaved', true);
                this.$log.info('Unsaved Page Difference: ', diff(cP, iP));
              }
            }
          });
        }
      });
    },
    makeURLStr(str = '') {
      const url = str.replace(/[^a-zA-Z0-9]/g, '-');
      return _.chain(url)
        .toLower()
        .replace(/ /g, '-')
        .value();
    },
    async goToNew() {
      this.loading = true;
      // prepare the empty page with new values
      const page = _.cloneDeep(newPage);
      page._id = this.guid();
      page.createdDate = new Date().toISOString();
      page.url = `${this.makeURLStr(this.name)}/`;
      page.name = this.name;
      page.templateID = this.templateID;
      if (this.currentSite.lifecycle === 'design') {
        page.status = 'published';
      }

      // If this page is supposed to be built from a template, then we need to
      // grab the template blocks and add them to the new page
      if (!_.isEmpty(this.templateID)) {
        const template = _.find(this.currentSite.templates, {
          _id: this.templateID,
        });
        page.blocks = _.cloneDeep(template.blocks);
      } else {
        const section = _.cloneDeep(newSection);
        section._id = this.guid();
        section.createdDate = new Date().toISOString();
        page.blocks = [section];
      }

      let type = 'success';
      let message = `${_.upperFirst(page.type)}: ${page.name} saved successfully!`;
      try {
        // add page to page type array in state
        this.$store.dispatch('addPage', page);
        this.$store.dispatch('addToSiteMap', page);
        // save all data to db
        await this.$lightning([{
          name: 'addPageItem',
          payload: {
            siteId: this.currentSite._id,
            siteMap: this.currentSite.siteMap,
            item: page,
          },
        }]);
        this.$log.debug('Successfully added page item');
        this.$store.dispatch('setOriginalPage', page);
        await this.$store.dispatch('setCurrentPage', page);
      } catch (e) {
        this.$log.error('error: ', e);
        type = 'error';
        message = `${e.message || e}. This has been reported.`;
        await this._reportError({
          error: e,
          fn: 'goToNew',
        });
      } finally {
        this.$store.dispatch('addNotification', {
          id: this.guid(),
          type,
          message,
        });
        this.loading = false;
        this._trackEvent({
          type: 'add_page',
          meta: {
            site_name: this.currentSite.alias,
            site_type: this.currentSite.type,
            site_tier: this.currentSite.tier,
            page_name: this.name,
            page_template_id: this.templateID,
            page_url: page.url,
          },
        });
        this.$emit('toggle-modal');
      }
    },
    async checkPage(choice) {
      if (choice === 'yes') {
        const isDroplet = !this._pageTypes.includes(this.currentPage.type);
        if (!isDroplet) {
          // revert the droplet count to the initial value
          await this.$store.dispatch('revertDropletCounts', {
            site: this.currentSite,
          });
          // revert all page data to the initial value
          await this.$store.dispatch('revertPage', this.initialCurrentPage);
        } else {
          // this will revert a droplet to when it was loaded
          const index = _.findIndex(this.currentSite.recycled, r => r._id === this.currentPage._id);
          const $vm = this;
          this.$store.dispatch('runStateFn', {
            fn() {
              $vm.currentSite.recycled.splice(index, 1, $vm.initialCurrentPage);
            },
          });
        }
        this.$store.dispatch('updateInitialPage');
        this.goToNew();
      }
      if (choice === 'no') {
        this.$emit('toggle-modal');
      }
    },
    pageNameCheck(name) {
      // check all pages for this site
      const valid = this.savedPages.includes(name);
      if (valid) {
        this.pageNameCheckError = 'There is another page with that name, please try another.';
      } else {
        this.pageNameCheckError = false;
      }
      return new Promise((resolve) => {
        resolve(!valid);
      });
    },
    close() {
      this.$emit('toggle-modal');
    },
    addPage() {
      this.$emit('toggle-modal');
    },
    clearAll() {
      this.name = '';
      this.description = '';
      this.clearSelectedLayouts();
    },
    formatErrors(ers) {
      let string = '';
      _.forEach(ers, (er) => {
        string = string += ` ${er}`;
      });
      return string;
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/add-page/index.js